import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AppContext';
import { useIsMobile } from '../hooks/useIsMobile';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, Search, RefreshCw, ShieldCheck, Shield, Eye, EyeOff } from 'lucide-react';

const roleColor = r => ({ admin: 'badge-purple', viewer: 'badge-blue', operator: 'badge-amber', partner: 'badge-gray' }[r] || 'badge-gray');

function lendersLabel(u) {
  return !u.is_superuser && !u.is_staff && !!u.company_id
    ? `Partner: ${u.partner_identifier || '—'}`
    : (u.allowedLenders?.length > 0 ? u.allowedLenders.join(', ') : 'All');
}

function UserModal({ user, onClose, onSaved }) {
  const isMobile = useIsMobile();
  const [form, setForm] = useState(
    user
      ? { email: user.email || '', password: '', first_name: user.first_name || '', last_name: user.last_name || '', is_staff: user.is_staff !== false, is_active: user.is_active !== false, group_ids: [], company_id: user.company_id ?? '', require_password_change: true }
      : { email: '', password: '', first_name: '', last_name: '', is_staff: false, is_active: true, group_ids: [], company_id: '', require_password_change: true }
  );
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [companies, setCompanies] = useState([]);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get('/auth/groups')
      .then(res => setAvailableGroups(Array.isArray(res.data) ? res.data : []))
      .catch(() => setAvailableGroups([]));
  }, []);

  useEffect(() => {
    let ignore = false;
    api.get('/companies/', { params: { limit: 50, page_size: 50, per_page: 50 } }).then(res => {
      if (!ignore) {
        const list = Array.isArray(res.data) ? res.data : (res.data?.items || res.data?.companies || res.data?.data || []);
        setCompanies(list);
      }
    }).catch(() => {});
    return () => { ignore = true; };
  }, []);

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, company_id: form.company_id === '' ? null : Number(form.company_id) };
      if (user && !payload.password) {
        delete payload.password;
        delete payload.require_password_change;
      }
      if (user) {
        await api.patch(`/auth/users/${user.id}`, payload);
        toast.success('User updated successfully');
      } else {
        await api.post('/auth/users', payload);
        toast.success('User created successfully');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.message || 'Error saving user');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <h2>{user ? 'Edit User' : 'Create User'}</h2>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">Email Address *</label>
              <input className="input" type="email" value={form.email}
                onChange={e => f('email', e.target.value)} required disabled={!!user} placeholder="user@kickoffadvertising.com" />
            </div>

            <div className="grid-2" style={isMobile ? { gridTemplateColumns: '1fr' } : undefined}>
              <div className="form-group">
                <label className="form-label">First Name</label>
                <input className="input" value={form.first_name} onChange={e => f('first_name', e.target.value)} placeholder="Austin" />
              </div>
              <div className="form-group">
                <label className="form-label">Last Name</label>
                <input className="input" value={form.last_name} onChange={e => f('last_name', e.target.value)} placeholder="Smith" />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">
                {user ? 'New Password (leave empty to keep current)' : 'Password *'}
              </label>
              <div style={{ position: 'relative' }}>
                <input className="input" type={showPwd ? 'text' : 'password'}
                  value={form.password} onChange={e => f('password', e.target.value)}
                  required={!user} placeholder="••••••••" style={{ paddingRight: 42 }} />
                <button type="button" onClick={() => setShowPwd(s => !s)}
                  style={{ position: 'absolute', right: 12, top: '50%',
                    transform: 'translateY(-50%)', background: 'none', border: 'none',
                    cursor: 'pointer', color: 'var(--text-muted)' }}>
                  {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
              {(!user || form.password) && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginTop: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.require_password_change}
                    onChange={e => f('require_password_change', e.target.checked)} />
                  <span>Require password change on next login</span>
                </label>
              )}
            </div>

            <div className="grid-2" style={isMobile ? { gridTemplateColumns: '1fr' } : undefined}>
              <div className="form-group">
                <label className="form-label">Staff Access (is_staff)</label>
                <select className="select" value={String(form.is_staff)}
                  onChange={e => f('is_staff', e.target.value === 'true')}>
                  <option value="false">Regular User (No Admin Tools)</option>
                  <option value="true">Staff (Access Admin Tools & API)</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Account Status</label>
                <select className="select" value={String(form.is_active)}
                  onChange={e => f('is_active', e.target.value === 'true')}>
                  <option value="true">Active</option>
                  <option value="false">Inactive / Suspended</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Company</label>
              <select className="select" value={form.company_id}
                onChange={e => f('company_id', e.target.value)}>
                <option value="">— None (Internal) —</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {availableGroups.length > 0 && (
              <div className="form-group">
                <label className="form-label">Assign Groups / Roles</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, background: 'var(--bg-raised)', padding: 10, borderRadius: 8, border: '1px solid var(--border)' }}>
                  {availableGroups.map(g => (
                    <label key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                      <input type="checkbox" checked={form.group_ids.includes(g.id)}
                        onChange={e => {
                          const updated = e.target.checked
                            ? [...form.group_ids, g.id]
                            : form.group_ids.filter(id => id !== g.id);
                          f('group_ids', updated);
                        }} />
                      <span>{g.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
              {saving ? <div className="spinner" /> : (user ? 'Save Changes' : 'Create User')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function UserCard({ u, isAdmin, isMe, onEdit, onDelete }) {
  const displayName = u.first_name || u.last_name ? `${u.first_name} ${u.last_name}`.trim() : u.email;
  const initial = (u.email || '?')[0].toUpperCase();

  return (
    <div className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12, background: isMe ? 'var(--bg-raised)' : undefined }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <div style={{
            width: 36, height: 36, borderRadius: '50%', background: 'var(--accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--accent-fg)', fontSize: 13, fontWeight: 700,
            fontFamily: 'var(--font-display)', flexShrink: 0,
          }}>
            {initial}
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayName}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{u.email}</div>
            {u.company_name && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{u.company_name}</div>}
          </div>
        </div>
        {isAdmin && (
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button className="btn btn-ghost btn-icon btn-sm" onClick={onEdit}><Pencil size={13} /></button>
            <button className="btn btn-danger btn-icon btn-sm" disabled={isMe} onClick={onDelete}><Trash2 size={13} /></button>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <span className={`badge ${u.is_superuser ? 'badge-purple' : u.is_staff ? 'badge-amber' : 'badge-blue'}`}>
          {u.is_superuser ? <ShieldCheck size={11} /> : <Shield size={11} />}
          {' '}{u.is_superuser ? 'Superuser' : u.is_staff ? 'Staff' : 'Regular User'}
        </span>
        <span className={`badge ${u.is_active !== false ? 'badge-green' : 'badge-red'}`}>
          {u.is_active !== false ? 'Active' : 'Inactive'}
        </span>
        {u.groups && u.groups.map(g => (
          <span key={g} className="badge badge-purple">{g}</span>
        ))}
      </div>
    </div>
  );
}

export default function UsersPage() {
  const isMobile = useIsMobile();
  const { user: me, isAdmin } = useAuth();
  const [data, setData]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [q, setQ]             = useState('');
  const [loading, setLoading] = useState(false);
  const [modal, setModal]     = useState(null);
  const [delId, setDelId]     = useState(null);
  const [companies, setCompanies] = useState([]);
  const [companyFilter, setCompanyFilter] = useState('');

  useEffect(() => {
    api.get('/companies/', { params: { limit: 50, page_size: 50, per_page: 50 } }).then(res => {
      const list = Array.isArray(res.data) ? res.data : (res.data?.items || res.data?.companies || res.data?.data || []);
      setCompanies(list);
    }).catch(() => {});
  }, []);

  const fetchData = useCallback(async (search = '', compId = '') => {
    setLoading(true);
    try {
      const { data: res } = await api.get('/auth/users', {
        params: { limit: 100, page_size: 100, ...(search ? { search } : {}), ...(compId ? { company_id: compId } : {}) },
      });
      const items = Array.isArray(res) ? res : res.items || [];
      setData(items);
      setTotal(res.count || items.length);
    } catch { toast.error('Error loading users'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(q, companyFilter); }, [fetchData, companyFilter]);

  const handleDelete = async id => {
    if (id === me?.id) { toast.error('You cannot delete your own user account'); return; }
    try {
      await api.delete(`/auth/users/${id}`);
      toast.success('User deleted');
      fetchData(q, companyFilter);
    } catch (err) { toast.error(err.response?.data?.detail || err.response?.data?.message || 'Error deleting user'); }
    setDelId(null);
  };

  return (
    <div className="fade-in page-shell">
      <div className="page-header" style={{ marginBottom: 20 }}>
        <h1>User & Role Management</h1>
        <p>{total} registered system users · Managed via Django RBAC System</p>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <form onSubmit={e => { e.preventDefault(); fetchData(q, companyFilter); }} style={{ flex: isMobile ? '1 1 100%' : 1 }}>
          <div className="search-bar">
            <Search size={14} className="search-icon" />
            <input className="input" placeholder="Search users by email or name…"
              value={q} onChange={e => setQ(e.target.value)} />
          </div>
        </form>
        <select className="select" style={{ maxWidth: isMobile ? '100%' : 220 }}
          value={companyFilter} onChange={e => setCompanyFilter(e.target.value)}>
          <option value="">All Companies</option>
          {companies.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <button className="btn btn-ghost" onClick={() => fetchData(q, companyFilter)}><RefreshCw size={14} /></button>
        {isAdmin && (
          <button className="btn btn-primary" onClick={() => setModal('create')} style={{ flex: isMobile ? 1 : undefined, justifyContent: isMobile ? 'center' : undefined }}>
            <Plus size={14} /> New User
          </button>
        )}
      </div>

      {loading && data.length === 0 ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <div className="spinner" style={{ width: 28, height: 28, borderWidth: 3 }} />
        </div>
      ) : isMobile ? (
        data.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>No users found</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {data.map(u => (
              <UserCard
                key={u.id}
                u={u}
                isAdmin={isAdmin}
                isMe={u.id === me?.id}
                onEdit={() => setModal(u)}
                onDelete={() => setDelId(u.id)}
              />
            ))}
          </div>
        )
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>User / Email</th><th>Company</th><th>Staff Access (is_staff)</th><th>Status</th>
                <th>Groups / Roles</th>
                {isAdmin && <th style={{ width: 100 }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>
                  No users found
                </td></tr>
              ) : data.map(u => (
                <tr key={u.id} style={{ background: u.id === me?.id ? 'var(--bg-raised)' : '' }}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%', background: 'var(--accent)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'var(--accent-fg)', fontSize: 12, fontWeight: 700,
                        fontFamily: 'var(--font-display)', flexShrink: 0,
                      }}>
                        {(u.email || '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600 }}>{u.first_name || u.last_name ? `${u.first_name} ${u.last_name}`.trim() : u.email}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{u.email} {u.id === me?.id && '(you)'}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{u.company_name || '—'}</td>
                  <td>
                    <span className={`badge ${u.is_superuser ? 'badge-purple' : u.is_staff ? 'badge-amber' : 'badge-blue'}`}>
                      {u.is_superuser ? <ShieldCheck size={11} /> : <Shield size={11} />}
                      {' '}{u.is_superuser ? 'Superuser' : u.is_staff ? 'Staff (is_staff)' : 'Regular User'}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${u.is_active !== false ? 'badge-green' : 'badge-red'}`}>
                      {u.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                    {u.groups && u.groups.length > 0 ? (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                        {u.groups.map(g => <span key={g} className="badge badge-purple">{g}</span>)}
                      </div>
                    ) : '—'}
                  </td>
                  {isAdmin && (
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setModal(u)}>
                          <Pencil size={13} />
                        </button>
                        <button className="btn btn-danger btn-icon btn-sm"
                          disabled={u.id === me?.id} onClick={() => setDelId(u.id)}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(modal === 'create' || (modal && modal.id)) && (
        <UserModal
          user={modal === 'create' ? null : modal}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); fetchData(q, companyFilter); }}
        />
      )}

      {delId && (
        <div className="modal-overlay" onClick={() => setDelId(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="modal-header"><h2>Delete User</h2></div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)' }}>Are you sure you want to delete this user account?</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost btn-sm" onClick={() => setDelId(null)}>Cancel</button>
              <button className="btn btn-danger btn-sm" onClick={() => handleDelete(delId)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

